import { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { ArrowRight, X, CheckCircle, SpinnerGap } from '@phosphor-icons/react';

const API_URL =
  typeof window !== 'undefined' && window.location.hostname &&
  !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL;

/**
 * WaitlistButton — pill CTA that opens an inline modal capturing email + name.
 * Drop-in replacement for the old "Try Free" / "Start for free" buttons during
 * the closed-beta period.
 */
export const WaitlistButton = ({
  label = 'Join Waitlist',
  source = 'landing',
  variant = 'primary',
  testId = 'waitlist-cta',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/waitlist`, {
        email: email.trim(),
        name: name.trim() || null,
        source,
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save — try again');
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => {
      setDone(false);
      setEmail('');
      setName('');
      setError('');
    }, 200);
  };

  // Style matrix
  const baseStyles =
    variant === 'hero'
      ? 'group inline-flex items-center gap-2 rounded-full bg-blue-600 text-white px-8 py-4 text-[15px] font-semibold transition-all hover:scale-[1.03] shadow-2xl'
      : variant === 'header'
      ? 'inline-flex items-center gap-1.5 rounded-full bg-white text-slate-900 px-4 sm:px-5 py-2 sm:py-2.5 text-[12px] sm:text-[13px] font-semibold hover:bg-slate-100 transition-all shadow-lg whitespace-nowrap'
      : 'inline-flex items-center gap-2 rounded-full bg-blue-600 text-white px-6 py-3 text-[14px] font-semibold hover:bg-blue-700 transition-all shadow-lg';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={testId}
        className={`${baseStyles} ${className}`}
        style={
          variant === 'hero'
            ? { boxShadow: '0 4px 30px rgba(37,99,235,0.45), 0 8px 40px rgba(0,0,0,0.2)' }
            : undefined
        }
      >
        {label}
        <ArrowRight size={variant === 'header' ? 14 : 16} weight="bold" />
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
          data-testid="waitlist-modal"
          onClick={close}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          {/* Card */}
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl bg-white p-7 sm:p-8 shadow-2xl"
          >
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              data-testid="waitlist-close"
            >
              <X size={18} weight="bold" />
            </button>

            {!done ? (
              <>
                <div className="inline-block px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-[11px] font-bold uppercase tracking-widest mb-3">
                  Closed beta
                </div>
                <h3 className="text-2xl font-bold text-slate-900 leading-tight">
                  Get early access to LifeAdmin AI
                </h3>
                <p className="mt-2 text-sm text-slate-600">
                  We're onboarding a small group of users first. Drop your email
                  and we'll send you an invite as soon as a spot opens up.
                </p>

                <form onSubmit={submit} className="mt-5 space-y-3">
                  <input
                    type="email"
                    required
                    autoFocus
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    data-testid="waitlist-email"
                  />
                  <input
                    type="text"
                    placeholder="First name (optional)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    data-testid="waitlist-name"
                  />
                  {error && (
                    <p className="text-xs text-red-600" data-testid="waitlist-error">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting || !email.trim()}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 text-white py-3 text-[15px] font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="waitlist-submit"
                  >
                    {submitting ? (
                      <>
                        <SpinnerGap size={16} className="animate-spin" /> Saving…
                      </>
                    ) : (
                      <>
                        Join the waitlist <ArrowRight size={16} weight="bold" />
                      </>
                    )}
                  </button>
                  <p className="text-[11px] text-slate-400 text-center">
                    No spam. We'll only email you when your invite is ready.
                  </p>
                </form>
              </>
            ) : (
              <div className="text-center py-3" data-testid="waitlist-success">
                <div className="inline-flex w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 items-center justify-center mb-4">
                  <CheckCircle size={32} weight="fill" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">You're on the list!</h3>
                <p className="mt-2 text-sm text-slate-600">
                  We'll email <span className="font-semibold text-slate-900">{email}</span> the
                  moment your invite is ready. Thanks for your patience.
                </p>
                <button
                  type="button"
                  onClick={close}
                  className="mt-5 px-5 py-2.5 rounded-xl bg-slate-100 text-slate-900 text-sm font-semibold hover:bg-slate-200 transition-colors"
                  data-testid="waitlist-done-close"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
